import { Subscription } from 'rxjs';
import { ChangeDetectorRef, Component, ContentChild, Input, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { ControlValueAccessor, FormControl, NgControl } from '@angular/forms';
import { MatSelect } from '@angular/material/select';
import { SizeProp } from 'src/app/types/style-tapes';
import { ColorSchemes } from 'src/styles/color-scheme.type';
import { SearchHlp } from 'src/lib/fliko/helpers/search.helper';
import { AsyncHlp } from 'src/app/shared/helpers/async.helper';
import { SelectInputData } from 'src/app/shared/interfaces/select-input-data.interface';
import { LocLabel, LocLabelModifier } from '../../clickable-row-table/models/data-table-config.models';
import { LegacyLabel } from '../common/legacy-label';

@Component({
  selector: 'app-select-input',
  templateUrl: './select-input.component.html',
  styleUrls: ['./select-input.component.scss']
})
export class SelectInputComponent<T> extends LegacyLabel implements ControlValueAccessor, OnInit, OnDestroy {
  public readonly DEFAULT_WIDTH: SizeProp = '100%';

  @Input() disabled = false;
  @Input() prefixIcon?: string;
  @Input() multiple?: boolean;
  @Input() set options(input: SelectInputData<T>[]) {
    if (input) {
      this._options = input;
      this.filterOptions();
      if (this._value && !this.selectedOption) {
        this.selectedOption = this.filteredOptions?.find((o) => o.value == this._value) ?? null;
      }
    }
  }
  @Input() placeholderKey: string | LocLabel | null | undefined;
  @Input() noEntriesLabelKey: string | LocLabel | null | undefined;
  @Input() searchable = false;
  @Input() required = false;
  @Input() outsideLabel = true;
  @Input() outline = true;
  @Input() footer = true;
  @Input() maxHeight: 'short' | 'medium' | 'long' = 'short';
  @Input() width?: SizeProp = this.DEFAULT_WIDTH;
  @Input() colors: ColorSchemes = ColorSchemes.PRIMARY;

  @ViewChild('selectSingle') private selectSingleRef!: MatSelect;
  @ViewChild('selectMulti') private selectMultiRef!: MatSelect;

  @ContentChild(TemplateRef) child!: TemplateRef<any>;

  public readonly EMPTY_OPTION = { title: '', value: '' };

  public searchControl = new FormControl();
  public filteredOptions: SelectInputData<T>[] = [];
  public selectedOption: SelectInputData<T> | null = null;

  private _value!: T;
  private _options: SelectInputData<T>[] = [];
  private subscription$: Subscription = new Subscription();

  ColorSchemes = ColorSchemes;
  LocLabelModifier = LocLabelModifier;
  constructor(
    public ngControl: NgControl,
    private cdr: ChangeDetectorRef
  ) {
    super();
    ngControl.valueAccessor = this;
  }

  ngOnInit(): void {
    if (this.searchable) {
      this.subscription$.add(this.searchControl.valueChanges.subscribe(() => AsyncHlp.inNextTick(() => this.filterOptions())));
    }
  }

  ngOnDestroy(): void {
    this.subscription$?.unsubscribe();
  }

  // this method sets the value programmatically
  // tslint:disable-next-line:typedef
  public writeValue(value: T) {
    if (value !== this._value) {
      this.selectedOption = this.filteredOptions?.find((o) => o.value == value) ?? null;
      this._value = value;
    }
  }

  get value(): T {
    return this._value;
  }
  set value(value: T) {
    this.writeValue(value);
    this.onChange(value);
    this.onTouch(value);
  }

  // upon UI element value changes, this method gets triggered
  // tslint:disable-next-line:typedef
  public registerOnChange(fn: any) {
    this.onChange = fn;
  }

  // upon touching the element, this method gets triggered
  // tslint:disable-next-line:typedef
  public registerOnTouched(fn: any) {
    this.onTouch = fn;
  }

  public onChange: any = (value: T) => {
    this.value = value;
  };
  public onTouch: any = () => {};

  public filterOptions(): void {
    if (this.searchControl?.value) {
      this.filteredOptions = SearchHlp.findAll(this._options, 'title', this.searchControl.value);
      this.cdr.detectChanges();
    } else {
      this.filteredOptions = this._options.slice();
    }
  }

  public close(): void {
    this.selectSingleRef?.close();
    this.selectMultiRef?.close();
  }
}
